import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BookOpen, Mail, Lock, User, Phone, Eye, EyeOff, Home, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { detectDefaultCountry } from '../lib/countryDialCodes';
import CountryDialPicker from '../components/auth/CountryDialPicker';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Same regex used on the Contact page — keep them aligned with the
// strict server-side validate_email_strict util.
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
// Strict client-side phone format: REQUIRES leading '+' and a country code
// followed by 7–16 digits. The backend then runs Google's libphonenumber
// validator which actually rejects fake/random numbers (e.g. +1234567890
// is rejected because no real country has that numbering plan).
const PHONE_DIGITS_RE = /^\+\d{8,16}$/;

const Signup = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // Country code dropdown auto-selects from browser locale; user-editable.
  const [countryDial, setCountryDial] = useState('+1');
  // National number — JUST the digits AFTER the dial code (the dropdown
  // contributes the country code). On submit we concatenate
  // `${countryDial}${phone}` and run libphonenumber on the backend.
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();

  // Detect the user's likely country once on mount and set the dropdown
  // default. Users on Philippine browsers see +63, US users see +1, etc.
  useEffect(() => {
    try {
      const detected = detectDefaultCountry();
      if (detected) setCountryDial(detected.dial);
    } catch {
      /* Fall back to default (US) — already set above */
    }
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    setErrMsg('');
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    if (trimmedName.length < 2) {
      setErrMsg('Please enter your full name.');
      return;
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      setErrMsg('Please enter a valid email address (e.g. you@gmail.com).');
      return;
    }
    if (!trimmedPhone) {
      setErrMsg('Please enter your phone number so the admin can reach you about your account.');
      return;
    }
    // Strip all formatting chars from the national portion + any leading '+'
    // (in case the user pasted a full E.164 number into the national field
    // by mistake — we'll re-stitch with the dropdown's dial code below).
    const nationalDigits = trimmedPhone.replace(/[\s\-().+]/g, '');
    if (!/^\d{6,15}$/.test(nationalDigits)) {
      setErrMsg('Please enter your phone number (digits only after the country code, 6–15 digits).');
      return;
    }
    // Stitch country code + national number → full E.164 candidate for the
    // backend libphonenumber validator. e.g. "+1" + "2025550150" → "+12025550150".
    const fullE164Candidate = `${countryDial}${nationalDigits}`;
    if (!PHONE_DIGITS_RE.test(fullE164Candidate)) {
      setErrMsg('Please enter your full phone number with a valid country code.');
      return;
    }
    if (password !== confirm) {
      setErrMsg('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/signup`, {
        name: trimmedName,
        email: trimmedEmail,
        phone: fullE164Candidate,
        password,
      });
      if (res.data?.success) {
        setSuccess(true);
        toast({
          title: 'Account created — pending review',
          description: res.data.message,
        });
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message || 'Signup failed';
      setErrMsg(msg);
      toast({
        title: 'Sign-up failed',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black flex items-center justify-center px-4 py-20" data-testid="signup-success-page">
        <div className="max-w-md w-full bg-white/5 border border-emerald-500/30 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-300 inline-flex items-center justify-center mb-4">
            <CheckCircle className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3" data-testid="signup-success-title">Pending admin approval</h1>
          <p className="text-gray-300 leading-relaxed mb-6">
            Thanks <span className="font-semibold text-white">{name.trim().split(' ')[0]}</span> — your account is in our review queue.
            We&apos;ll email <span className="text-fuchsia-300">{email}</span> the moment it&apos;s activated and you can sign in.
          </p>
          <p className="text-xs text-gray-500 mb-6">Approvals typically happen within one business day.</p>
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              data-testid="signup-success-home"
            >
              <Home className="h-4 w-4 mr-1.5" /> Back to home
            </Button>
            <Button
              onClick={() => navigate('/login')}
              className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
              data-testid="signup-success-login"
            >
              Go to sign-in
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black flex items-center justify-center px-4 py-20" data-testid="signup-page">
      {/* Atmospheric backdrop */}
      <div className="fixed inset-0 -z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-fuchsia-700/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-amber-600/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1.5 mb-6"
          data-testid="signup-back-home"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </button>

        <div className="bg-white/5 border border-white/10 backdrop-blur-md rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-lg bg-fuchsia-500/15 text-fuchsia-300 inline-flex items-center justify-center">
              <BookOpen className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-bold text-white">Start your 7-day free trial</h1>
          </div>

          <p className="text-sm text-gray-400 leading-relaxed mb-6">
            Get <span className="text-fuchsia-300">7 days of free access</span> to every book on
            Seven Chapter Books. New accounts are reviewed by our admin team before sign-in is
            enabled — we&apos;ll email you the moment yours is activated.
          </p>

          <form onSubmit={handleSignup} className="space-y-4" data-testid="signup-form">
            <div>
              <Label htmlFor="name" className="text-gray-300 text-sm">Full name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  minLength={2}
                  className="pl-10 bg-zinc-900 border-white/10 text-white"
                  placeholder="Your full name"
                  data-testid="signup-name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-300 text-sm">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 bg-zinc-900 border-white/10 text-white"
                  placeholder="you@example.com"
                  data-testid="signup-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone" className="text-gray-300 text-sm">Phone number</Label>
              <div className="flex items-stretch gap-2 mt-1">
                {/* Searchable country dial-code picker — auto-selected from browser locale */}
                <CountryDialPicker
                  value={countryDial}
                  onChange={setCountryDial}
                  testId="signup-country-dial"
                />
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className="pl-10 bg-zinc-900 border-white/10 text-white"
                    placeholder="2025550150"
                    data-testid="signup-phone"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Country code auto-detected. We verify it's a real number — fake or random numbers will be rejected.
              </p>
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-300 text-sm">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 pr-10 bg-zinc-900 border-white/10 text-white"
                  placeholder="At least 8 characters"
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Min 8 characters with uppercase, lowercase, number and symbol.</p>
            </div>

            <div>
              <Label htmlFor="confirm" className="text-gray-300 text-sm">Confirm password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  className="pl-10 bg-zinc-900 border-white/10 text-white"
                  placeholder="Repeat your password"
                  data-testid="signup-confirm"
                />
              </div>
            </div>

            {errMsg && (
              <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2" data-testid="signup-error">
                {errMsg}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-semibold py-5"
              data-testid="signup-submit"
            >
              {loading ? 'Starting trial…' : 'Start free trial'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-fuchsia-300 hover:text-fuchsia-200 underline underline-offset-2 font-medium" data-testid="signup-to-login">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
